import React, { useMemo, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import moment from "moment";
import {
  LuAlertCircle,
  LuBoxes,
  LuBuilding2,
  LuDatabase,
  LuIndianRupee,
} from "react-icons/lu";

import AdditionalCostForm from "./AdditionalCostForm";
import MaterialLinesForm from "./MaterialLinesForm";
import MaterialPickerModal from "./MaterialPickerModal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formCardClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import {
  costsAmount,
  emptyLine,
  filledLines,
  formatAmount,
  formatQty,
  lineAmount,
  lineHints,
  sameLines,
  unlinkLines,
} from "../../static/materialLine";
import { closeAfterQty } from "../../static/cuttingLine";
import { iconSelectStyles } from "../../static/styles";
import {
  IMaterialLineInput,
  IMaterialLinesFormInput,
  MaterialOptionProps,
  PartyInfoProps,
  PartyOptionProps,
} from "../../types/materialLine";
import { OpenOrderProps, OrderLineProps } from "../../types/materialOrder";

type Props = {
  defaultValues?: IMaterialLinesFormInput;
  partyOptions: PartyOptionProps[];
  materials: MaterialOptionProps[];
  materialsLoading: boolean;
  partyInfo: PartyInfoProps[];
  // Called when another party is picked, so the page can load that party's last prices (and open orders).
  onPartyChange: (partyId: number) => void;
  // An order with purchases keeps its party.
  partyLocked?: boolean;
  // On an order being edited: the qty already received of each line, and the lines a purchase closed, by line key.
  received?: Record<string, number>;
  closed?: string[];
  // A purchase's lines may receive the party's open order lines (openOrders), which the picker offers first.
  isPurchase?: boolean;
  // Only a purchase line has a price and an amount, and only a purchase has additional costs, both only for a user
  // who may see rates.
  withPrice?: boolean;
  openOrders?: OpenOrderProps[];
  openOrdersLoading?: boolean;
  onSubmit: (data: IMaterialLinesFormInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  submitLabel: string;
};

// The form of a material order and of a material purchase. A purchase's lines can receive the party's open order lines,
// and only a purchase has prices, additional costs and an amount, for a user who may see rates.
const MaterialEntryForm = ({
  defaultValues,
  partyOptions,
  materials,
  materialsLoading,
  partyInfo,
  onPartyChange,
  partyLocked,
  received,
  closed,
  isPurchase = false,
  withPrice = false,
  openOrders,
  openOrdersLoading,
  onSubmit,
  isSubmitting,
  onCancel,
  submitLabel,
}: Props) => {
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<IMaterialLinesFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: moment().format("YYYY-MM-DDTHH:mm"),
      items: [{ ...emptyLine }],
      additional_costs: [],
    },
  });

  const { fields, remove, replace } = useFieldArray({ control, name: "items" });

  const partyId = useWatch({ control, name: "party_id" });
  const watchedItems = useWatch({ control, name: "items" });
  const watchedCosts = useWatch({ control, name: "additional_costs" });

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const hints = useMemo(
    () => lineHints(partyInfo, received, closed),
    [partyInfo, received, closed]
  );

  // The open orders' lines, each a card of its own in the picker, oldest order first.
  const orderLines = useMemo(
    (): OrderLineProps[] =>
      (openOrders || []).flatMap((order) =>
        order.items.map((item) => ({ ...item, order_date: order.date }))
      ),
    [openOrders]
  );

  const orderLineById = useMemo(
    () => new Map(orderLines.map((line) => [line.id, line])),
    [orderLines]
  );

  // Puts new lines in the form (from the picker, or without their order lines). An empty list leaves the one empty
  // row a form always has; errors of the old rows would point at the wrong ones, so they go.
  const setLines = (lines: IMaterialLineInput[]) => {
    const next = lines.length > 0 ? lines : [{ ...emptyLine }];

    if (sameLines(next, getValues("items"))) return;

    replace(next);
    clearErrors("items");
  };

  // A purchase row's qty that comes to make up its order line (with what other purchases received) ticks "ઓર્ડર બંધ",
  // and one that no longer does unticks it, as on the picker's cards; a tick set by hand otherwise stays.
  const handleQtyChange = (index: number, previous: string, qty: string) => {
    const orderLine = orderLineById.get(
      getValues(`items.${index}.order_item_id`)
    );

    if (!orderLine) return;

    const closeOrder = getValues(`items.${index}.close_order`);
    const next = closeAfterQty(
      orderLine.qty,
      orderLine.received_qty,
      previous,
      qty,
      closeOrder
    );

    if (next !== closeOrder) setValue(`items.${index}.close_order`, next);
  };

  const handlePickerClose = (lines: IMaterialLineInput[]) => {
    setIsPickerOpen(false);

    // Untouched empty rows don't survive the picker, but the form keeps its other rows.
    if (!sameLines(lines, filledLines(getValues("items")))) {
      setLines(lines);
    }
  };

  const totalQty = (watchedItems || []).reduce(
    (sum, item) => sum + (Number(item?.qty) || 0),
    0
  );

  // The purchase's amount: its lines' amounts and its additional costs, as the party account gets it.
  const totalAmount =
    (watchedItems || []).reduce(
      (sum, item) => sum + (item ? lineAmount(item) : 0),
      0
    ) + costsAmount(watchedCosts);

  const handleOnSubmit: SubmitHandler<IMaterialLinesFormInput> = (data) => {
    onSubmit(data);
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="material-entry-party" className={fieldLabelClass}>
                પાર્ટી પસંદ કરો <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.party_id && "error")}>
                <LuBuilding2 className={formSelectIconClass} />
                <Controller
                  control={control}
                  name="party_id"
                  rules={{ required: true, min: 1 }}
                  render={({ field }) => (
                    <Select
                      inputId="material-entry-party"
                      ref={field.ref}
                      options={partyOptions}
                      value={
                        partyOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(option: any) => {
                        const value = option?.value || 0;

                        if (value === field.value) return;

                        field.onChange(value);

                        // Another party's orders: the lines stay, without the order lines they received.
                        const items = getValues("items");

                        if (items.some((item) => item.order_item_id > 0)) {
                          setLines(unlinkLines(items));
                        }

                        onPartyChange(value);
                      }}
                      isDisabled={partyLocked}
                      placeholder="પાર્ટી સિલેક્ટ કરો..."
                      noOptionsMessage={() => "મટીરીયલ ની પાર્ટી મળી નથી"}
                      styles={iconSelectStyles}
                      isSearchable
                      className={cx(
                        "react-select-container",
                        errors.party_id && "error"
                      )}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {errors.party_id ? (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટી સિલેક્ટ કરો
                </p>
              ) : (
                partyLocked && (
                  <p className="text-xs text-gray-500">
                    આ ઓર્ડર ની ખરીદી થઈ ગઈ હોવાથી પાર્ટી બદલી શકાશે નહીં
                  </p>
                )
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="material-entry-date" className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.date && "error")}>
                <LuDatabase className={formSelectIconClass} />
                <Input
                  id="material-entry-date"
                  type="datetime-local"
                  className={fieldInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.date && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  તારીખ અને સમય દાખલ કરો
                </p>
              )}
            </div>
          </div>

          <MaterialLinesForm
            control={control}
            fields={fields}
            errors={errors}
            onRemove={remove}
            onQtyChange={handleQtyChange}
            onOpenPicker={() => setIsPickerOpen(true)}
            materials={materials}
            hints={hints}
            hasParty={!!partyId}
            isPickerLoading={isPurchase && !!openOrdersLoading}
            isPurchase={isPurchase}
            orderLineById={orderLineById}
            withPrice={withPrice}
          />

          {isPurchase && withPrice && (
            <AdditionalCostForm
              control={control}
              register={register}
              errors={errors}
            />
          )}

          {isPurchase && (
            <div className="rounded-lg border border-[#113F67]/15 bg-[#113F67]/5 px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-[#113F67]">
                <LuBoxes className="h-4 w-4" />
                કુલ જથ્થો : {formatQty(totalQty)}
              </span>
              {withPrice && (
                <span className="flex items-center gap-2 text-sm font-semibold text-[#113F67]">
                  <LuIndianRupee className="h-4 w-4" />
                  કુલ રકમ : {formatAmount(totalAmount)}
                </span>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {isPickerOpen && (
        <MaterialPickerModal
          materials={materials}
          materialsLoading={materialsLoading}
          initialLines={getValues("items")}
          hints={hints}
          isPurchase={isPurchase}
          orderLines={orderLines}
          withPrice={withPrice}
          onClose={handlePickerClose}
        />
      )}
    </div>
  );
};

export default MaterialEntryForm;
